import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, difference, isEqual } from 'lodash';
import { Link, useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { Button, Modal, IWarning, FormSection, FormInput } from '@praktio/common-ui';

import { RootState } from '../../ducks/store';
import {
  clearUser,
  getUser,
  updateUser,
  addUser,
  deleteUser,
  expirePassword,
  update,
  getUsers,
} from '../../ducks/users';
import {
  Days,
  DuplicateEmailFormWarning,
  ErrorCodes,
  GenericFormWarning,
  InvalidPasswordFormWarning,
  SamePasswordFormWarning,
  NotificationFrequency,
  Roles,
  InvalidEmailAdminWarning,
  FormValidationErrors,
} from '../../utils/constants';

import CommonUser from '../common/User';

import { getTeam, getTeams } from '../../ducks/teams';
import AssignCourseModal from './AssignCourseModal';
import UserRoles from './Roles';
import SendWelcomeEmailModal from './SendWelcomeEmailModal';
import { findFormErrors, handleUserFormEdit, updateSearchParams } from '../../utils/functions';

import { RightPaneHeader } from '../common/RightPane/RightPane';
import UserCourseAssignments from './UserCourseAssignments/UserCourseAssignments';
import { TopNavBranding, TopNavBrandingPreviewWrapper } from '../TopNav';
import ResetProgressModal from './modals/ResetProgressModal/ResetProgressModal';
import OverlaySpinner from '../common/OverlaySpinner/OverlaySpinner';
import { useGetTeamCourses } from '../../hooks/useGetTeamCourses';

import './User.scss';

// TODO: Move these Modals somewhere else
const DeleteUserModal = ({
  user,
  onHide,
  show,
}: {
  user?: IUser;
  show: boolean;
  onHide: () => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { teamId } = useParams();
  const [searchParams] = useSearchParams();
  const isTeamPage = !!location?.pathname.match(/^\/admin\/teams\/\w{24}$/gi);
  let navUrl = '/admin/users';
  if (isTeamPage) {
    navUrl = location.pathname;
    let i = 0;
    for (const entry of searchParams?.entries()) {
      const [key, value] = entry;
      if (key !== 'userId') {
        if (!i) {
          navUrl += '?';
        } else {
          navUrl += '&';
        }

        navUrl += `${key}=${value}`;
      }
    }
  }

  return (
    <Modal
      onHide={onHide}
      onSave={() => {
        if (user && user._id) {
          dispatch(deleteUser(user?._id, navigate, navUrl, teamId));
        }
      }}
      show={show}
      confirmationString={'DELETE'}
      title={`Delete User`}
      description={`Are you sure you want to delete the user "${user?.firstName} ${user?.lastName}?" This is permanent and data cannot be recovered!`}
    />
  );
};

const ExpirePasswordModal = ({
  user,
  onHide,
  show,
}: {
  user: IUser;
  show: boolean;
  onHide: () => void;
}) => {
  const dispatch = useDispatch();
  return (
    <Modal
      onHide={onHide}
      onSave={() => {
        if (user && user._id) {
          dispatch(expirePassword(user._id));
        }
      }}
      show={show}
      confirmationString={'EXPIRE'}
      title={`Expire Password`}
      description={`Are you sure you want to set the user "${user.firstName} ${user.lastName}" password to expired? This is permanent and user will need to reset their password!`}
    />
  );
};

interface IAdminUserProps {
  additionalSaveAction?: any;
  closeButtonAction: () => void;
}

const User = ({ additionalSaveAction, closeButtonAction }: IAdminUserProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const currentTab = searchParams.get('tab');
  const isNew = userId === 'new';
  // DEV NOTE: Don't believe the teamId will be coming from the searchParams
  // (what query.get appears to be pulling from). Commenting this out and testing/using
  // the useParams version instead.
  // Want to leave this here in case this is untrue and we need it back.
  // const teamId = query.get('teamId') || undefined;

  const { teamId } = useParams();
  const dispatch = useDispatch();
  const { initialData, teams, teamName } = useSelector((state: RootState) => {
    const initialData = state.users.current;
    const teams = state.teams.list || [];
    const team = teams.find((team) => team._id === teamId);
    const teamName = team?.name;
    return {
      initialData,
      teams,
      teamName,
    };
  });

  const [showAssignCourseModal, setShowAssignCourseModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showExpirePasswordModal, setShowExpirePasswordModal] = useState(false);
  const [showNotificationWarningModal, setShowNotificationWarningModal] = useState(false);
  const [showSendWelcomeEmailModal, setShowSendWelcomeEmailModal] = useState(false);
  const [showResetAllProgressModal, setShowResetAllProgressModal] = useState(false);
  const prevData = useRef<IUser>({} as IUser);
  const [user, setUser] = useState(initialData || ({} as IUser));
  const [hasChanges, setHasChanges] = useState(!isEqual(user, prevData.current));
  const [disableSave, setDisableSave] = useState(!hasChanges);
  const [savedChanges, setSavedChanges] = useState(false);
  const [warnings, setWarnings] = useState<IWarning[]>([]);
  const [userFormErrors, setUserFormErrors] = useState<IBootstrapFormErrors>();
  const [headerText, setHeaderText] = useState<string | undefined>();
  const [showOverlay, setShowOverlay] = useState(false);
  const [noCoursesSelected, setNoCoursesSelected] = useState(false);

  const { getTeamsCourseIds } = useGetTeamCourses();

  useEffect(() => {
    dispatch(getTeams());
  }, [dispatch]);

  useEffect(() => {
    if (initialData && !isEqual(prevData.current, initialData)) {
      setUser(initialData);
      setHasChanges(false);
      prevData.current = cloneDeep(initialData);
    }
  }, [initialData, warnings]);

  useEffect(() => {
    if (userId) {
      if (!isNew && userId !== initialData?._id) {
        setUser({});
        setHasChanges(false);
        setWarnings([]);
        setUserFormErrors({});
        dispatch(getUser(userId));
      } else if (
        isNew &&
        (initialData?._id || (teamId && !initialData?.roles?.find((r) => r.teamId === teamId)))
      ) {
        const newUser: IUser = {};
        if (teamId) {
          if (currentTab === 'learners') {
            newUser.roles = [{ role: Roles.Learner, teamId }];
          }
          if (currentTab === 'managers') {
            newUser.roles = [{ role: Roles.TeamManager, teamId, includeSubteams: true }];
          }
        }
        setHasChanges(false);
        setWarnings([]);
        setUserFormErrors({});
        dispatch(update({ current: newUser }));
      }
    }
  }, [initialData, isNew, userId, teamId, currentTab, dispatch]);

  useEffect(() => {
    setHeaderText(
      user.firstName || user.lastName
        ? `${user.firstName || ''} ${user.lastName || ''}`
        : '[New User]',
    );
  }, [user]);

  useEffect(() => {
    return () => {
      dispatch(clearUser());
    };
  }, [dispatch]);

  useEffect(() => {
    setDisableSave(!hasChanges);
    if (hasChanges && savedChanges) {
      setSavedChanges(false);
    }
  }, [hasChanges, savedChanges]);

  const applyChanges = ({
    clonedUser,
    clonedFormErrors,
    clonedWarnings,
  }: {
    clonedUser: IUser;
    clonedFormErrors?: IBootstrapFormErrors;
    clonedWarnings: IWarning[];
  }) => {
    const newHasChanges = !isEqual(clonedUser, prevData.current);

    // Queue/Execute setStates.
    setHasChanges(newHasChanges);
    setDisableSave(!newHasChanges);
    setUser(clonedUser);
    setUserFormErrors(clonedFormErrors);
    setWarnings(clonedWarnings);
  };

  const updateUserReportDeselectedCourses = (value: string[], user: IUser): void => {
    const prevReportTeams = prevData.current?.reportTeams || [];
    const addedTeams = difference(value as string[], prevReportTeams);
    const newTeamCoursesIds = getTeamsCourseIds(addedTeams);

    if (!newTeamCoursesIds.length) {
      return;
    }

    const newDeselectedCourses = difference(user.reportDeselectedCourses, newTeamCoursesIds);

    user.reportDeselectedCourses = newDeselectedCourses;
  };

  const updateNoCoursesSelected = (deselectedCourses: string[], user: IUser): void => {
    const teamCourseIds = getTeamsCourseIds(user.reportTeams || []);

    // If there are no teams or courses, then set noCoursesSelected to false (since there are no courses to select).
    if (!user?.reportTeams?.length && !teamCourseIds.length) {
      setNoCoursesSelected(false);
      return;
    }

    const noCoursesSelected = teamCourseIds.every((courseId) =>
      deselectedCourses.includes(courseId),
    );

    setNoCoursesSelected(noCoursesSelected);
  };

  const prepAndExecuteUserFormEdit = (field: string, value: UserFormValueOptions) => {
    let clonedUser = cloneDeep(user);
    let clonedFormErrors = cloneDeep(userFormErrors);
    let clonedWarnings = cloneDeep(warnings);

    handleUserFormEdit({ field, value, clonedUser, clonedFormErrors, clonedWarnings });

    // If field is "reportTeams", update reportDeselectedCourses to remove all courses from the newly selected teams.
    if (field === 'reportTeams') {
      updateUserReportDeselectedCourses(value as string[], clonedUser);
    }

    // If field is "reportDeselectedCourses", update noCoursesSelected state.
    if (field === 'reportDeselectedCourses') {
      updateNoCoursesSelected(value as string[], clonedUser);
    }

    applyChanges({ clonedUser, clonedFormErrors, clonedWarnings });
  };

  const prepAndExecuteMultipleUserFormEdits = (
    edits: { field: string; value: UserFormValueOptions }[],
  ) => {
    let clonedUser = cloneDeep(user);
    let clonedFormErrors = cloneDeep(userFormErrors);
    let clonedWarnings = cloneDeep(warnings);

    edits.forEach((edit) => {
      handleUserFormEdit({
        field: edit.field,
        value: edit.value,
        clonedUser,
        clonedFormErrors,
        clonedWarnings,
      });
    });

    applyChanges({ clonedUser, clonedFormErrors, clonedWarnings });
  };

  const handleSubmit = async (event?: React.MouseEvent<HTMLElement>) => {
    event?.preventDefault();

    setDisableSave(true);

    const newErrors = await findFormErrors({
      user: user,
      ignoreCurrentPassword: true,
      noCoursesSelected,
    });

    if (Object.keys(newErrors).length > 0) {
      setWarnings([...warnings, GenericFormWarning]);
      setUserFormErrors(newErrors);
    } else {
      if (
        // If the notification frequency used to be Never, but is being
        // set to an actual schedule, show the warning modal.
        prevData.current?.reportNotification?.frequency === NotificationFrequency.Never &&
        user?.reportNotification?.frequency !== NotificationFrequency.Never
      ) {
        setShowNotificationWarningModal(true);
      } else {
        try {
          // Editing an existing user
          if (user._id) {
            await dispatch(updateUser(user));
            setSavedChanges(true);
            // Creating a new user
          } else {
            const newlyAddedUser = (await dispatch(addUser(user))) as IUser;
            updateSearchParams({
              fields: [
                {
                  name: 'userId',
                  newValue: newlyAddedUser?._id,
                },
              ],
              searchParams: searchParams,
              setSearchParams: setSearchParams,
            });
            setSavedChanges(true);
          }

          if (teamId) {
            dispatch(getTeam(teamId));

            // When adding a new user for a Team, get the full list of users
            // again to have the most up-to-date data
            dispatch(getUsers());
          }

          if (additionalSaveAction) {
            dispatch(additionalSaveAction);
          }
        } catch (e) {
          const error = e as IError;
          const { errorCode } = error;
          let field: string | undefined;
          let newWarning: IWarning | undefined;
          let message = error.message;
          switch (errorCode) {
            case ErrorCodes.InvalidPassword:
              field = 'currentPassword';
              newWarning = InvalidPasswordFormWarning;
              break;
            case ErrorCodes.SamePassword:
              field = 'password';
              newWarning = SamePasswordFormWarning;
              break;
            case ErrorCodes.DuplicateEmail:
              field = 'email';
              newWarning = DuplicateEmailFormWarning;
              break;
            case ErrorCodes.InvalidEmail:
              field = 'email';
              newWarning = InvalidEmailAdminWarning;
              message = FormValidationErrors.invalidEmail;
              break;
            default:
              break;
          }

          if (field && newWarning) {
            setUserFormErrors({
              ...userFormErrors,
              [field]: message,
            });
            setWarnings([...warnings, newWarning]);
          }
        }
      }
    }
  };

  const onRoleChange = (oldRole?: IRole, newRole?: IRole) => {
    const updatedUser = cloneDeep(user);
    if (!updatedUser.roles) {
      updatedUser.roles = [];
    }

    const { roles } = updatedUser;
    let updated = false;
    // If newRole provided, it's either an update or new
    if (newRole) {
      // If old role provided, this is an update, so find and update old role
      if (oldRole) {
        // Verify that the new role doesn't exist if teamId and/or role
        // are changing
        const newIndex = roles?.findIndex((r) => {
          return r.role === newRole.role && r.teamId === newRole.teamId;
        });
        if (
          (newRole.role === oldRole.role && newRole.teamId === oldRole.teamId) ||
          newIndex === -1
        ) {
          const index =
            roles?.findIndex((r) => {
              return r.role === oldRole.role && r.teamId === oldRole.teamId;
            }) ?? -1;
          if (index > -1) {
            roles?.splice(index, 1, newRole);
            updated = true;
          }
        }
      }
      // Otherwise, verify new role doesn't already exist
      else {
        const index = roles?.findIndex((r) => {
          return r.role === newRole.role && r.teamId === newRole.teamId;
        });

        if (index === -1) {
          roles?.push(newRole);
          updated = true;
        }
      }
    }
    // No new role provided, so deleting old role
    else if (oldRole) {
      const index =
        roles?.findIndex((r) => {
          return r.role === oldRole.role && r.teamId === oldRole.teamId;
        }) ?? -1;
      if (index > -1) {
        roles?.splice(index, 1);
        updated = true;
      }
    }

    if (updated) {
      const hasTeamManagerRole = updatedUser.roles?.find((r) => r.role === Roles.TeamManager);
      if (hasTeamManagerRole && !updatedUser.reportNotification) {
        updatedUser.reportNotification = {
          frequency: NotificationFrequency.Weekly,
          day: Days.Monday,
        };
      } else if (!hasTeamManagerRole && updatedUser.reportNotification) {
        delete updatedUser.reportNotification;
      }
    }

    prepAndExecuteMultipleUserFormEdits([
      { field: 'roles', value: updatedUser.roles },
      { field: 'reportNotification', value: updatedUser.reportNotification },
    ]);
  };

  const onAssignmentRemoved = (assignment: ICourseAssignment) => {
    const updatedAssignments: ICourseAssignment[] = [...(user?.courseAssignments || [])];
    let index = updatedAssignments.findIndex(
      (a) => a.courseId === assignment.courseId && !a.teamId,
    );
    if (index > -1) {
      updatedAssignments.splice(index, 1);
    }

    const updatedCourses: ICourse[] = [...((user?.courses || []) as ICourse[])];
    index = updatedCourses.findIndex((c) => c._id === assignment.courseId && !c.teamId);
    if (index > -1) {
      updatedCourses.splice(index, 1);
    }

    prepAndExecuteMultipleUserFormEdits([
      { field: 'courseAssignments', value: updatedAssignments },
      { field: 'courses', value: updatedCourses },
    ]);
  };

  const passwordExpiration = user?.passwordUpdatedAt ? new Date(user.passwordUpdatedAt) : undefined;
  if (passwordExpiration) {
    passwordExpiration.setDate(passwordExpiration.getDate() + (user?.passwordExpirationSpan ?? 0));
  }

  return (
    <div className="User">
      <RightPaneHeader
        headerText={headerText || ''}
        savedChanges={savedChanges}
        unsavedChanges={hasChanges}
        closeButtonAction={closeButtonAction}
        mainButton={
          <Button
            disabled={disableSave}
            onClick={async (event) => {
              await handleSubmit(event);
            }}
            form="admin-user-form"
            btnStyle="primary"
          >
            SAVE
          </Button>
        }
      />
      {!!warnings &&
        warnings.length > 0 &&
        warnings.map((warning, i) => (
          <div key={`warning-${i}`} className="alert alert-danger" role="alert">
            <div className="alert-title">{warning.title}</div>
            <div className="alert-body">{warning.body}</div>
          </div>
        ))}
      <Form id="admin-user-form" onSubmit={() => {}}>
        <div>
          Last Login: {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'NEVER'}
        </div>
        <div>Password Expires: {passwordExpiration?.toLocaleString() || 'EXPIRED'}</div>
        <CommonUser
          handleFormEdit={prepAndExecuteUserFormEdit}
          userFormErrors={userFormErrors}
          user={user}
          isNew={isNew}
          teamId={teamId}
          teamName={teamName}
        />
        <FormSection sectionName="Branding" sectionDescription="Team Branding Logo">
          <div className="User-branding">
            {!!user?.teamLogo?.logo && (
              <>
                <TopNavBrandingPreviewWrapper>
                  <TopNavBranding
                    collaborationLogo={user?.teamLogo?.logo}
                    useWhiteFilter={user?.teamLogo?.useWhiteFilter}
                    className="d-flex"
                    isPreview
                  />
                </TopNavBrandingPreviewWrapper>
                <Link to={`/admin/teams/${user.teamLogo.teamId}`}>
                  {teams.find((t) => t._id === user?.teamLogo?.teamId)?.name}
                </Link>
              </>
            )}
            {!user?.teamLogo?.logo && <div>None</div>}
          </div>
        </FormSection>
        <FormSection
          sectionName="Course Assignments"
          sectionAddition={
            <Button onClick={() => setShowAssignCourseModal(true)}>ASSIGN COURSES</Button>
          }
        >
          <UserCourseAssignments
            user={user}
            teams={teams}
            onAssignmentRemoved={onAssignmentRemoved}
          />
        </FormSection>
        <FormSection sectionName="Roles">
          <UserRoles roles={user.roles || []} teams={teams} onApply={onRoleChange} />
        </FormSection>
        {!!user?.legacyData && (
          <FormSection sectionName="Other Info">
            {!!user?.legacyData && (
              <div>
                <h6>Legacy Data</h6>
                <div>
                  <label>OriginalId:</label> {user.legacyData.OriginalUserId}
                </div>
                <div>
                  <label>Id:</label> {user.legacyData.Id}
                </div>
                <div>
                  <label>Email:</label> {user.legacyData.Email}
                </div>
              </div>
            )}
          </FormSection>
        )}
        {!!user?.ssoData && (
          <FormSection sectionName="Single Sign-On Info">
            {Object.keys(user.ssoData).map((key, index) => {
              return (
                <FormInput
                  key={`sso-${index}`}
                  label={key}
                  fieldName={key}
                  value={user.ssoData[key] || ''}
                  readOnly
                />
              );
            })}
          </FormSection>
        )}
        <FormSection>
          <h4>User Actions</h4>
          <div role="group" className="d-flex gap-1 User-action-buttons">
            <Button
              disabled={!user?.passwordSet}
              onClick={() => setShowExpirePasswordModal(true)}
              btnStyle="danger"
              tooltip={!user?.passwordSet ? 'User does not have a password set' : undefined}
              tooltipPlacement="top"
            >
              EXPIRE PASSWORD
            </Button>
            <Button onClick={() => setShowDeleteUserModal(true)} btnStyle="danger">
              DELETE
            </Button>
            <Button onClick={() => setShowSendWelcomeEmailModal(true)}>SEND WELCOME EMAIL</Button>
            <Button onClick={() => setShowResetAllProgressModal(true)}>RESET ALL PROGRESS</Button>
          </div>
        </FormSection>
      </Form>
      <AssignCourseModal
        onApply={(courses) => {
          const updatedCourses: ICourse[] = [...((user?.courses as ICourse[]) || [])];
          courses.forEach((c) => {
            const index = updatedCourses.findIndex((uc) => c._id === uc._id && !uc.teamId);
            if (index === -1) {
              updatedCourses.push(c);
            }
          });

          prepAndExecuteUserFormEdit('courses', updatedCourses);
        }}
        onHide={() => setShowAssignCourseModal(false)}
        show={showAssignCourseModal}
        assignedCourses={((user.courses || []) as ICourse[]).filter((c) => !c.teamId)}
      />
      {!!user && (
        <>
          {' '}
          <DeleteUserModal
            show={showDeleteUserModal}
            onHide={() => setShowDeleteUserModal(false)}
            user={user}
          />
          <ExpirePasswordModal
            show={showExpirePasswordModal}
            onHide={() => setShowExpirePasswordModal(false)}
            user={user}
          />
          <SendWelcomeEmailModal
            onHide={() => setShowSendWelcomeEmailModal(false)}
            show={showSendWelcomeEmailModal}
            userId={user._id}
          />
          <ResetProgressModal
            show={showResetAllProgressModal}
            onHide={() => setShowResetAllProgressModal(false)}
            userId={user._id as string}
            email={user.email}
            setShowOverlay={setShowOverlay}
            all
          />
        </>
      )}
      <Modal
        title="Updating User's Report Notification"
        description="This user has a Report Notification set to Never. Updating this will enable notifications. Do you want to continue?"
        show={showNotificationWarningModal}
        onHide={() => setShowNotificationWarningModal(false)}
        // TODO - Get this behavior out of the modal and into the Form
        onSave={() => {
          if (user._id) {
            dispatch(updateUser(user));
          } else {
            dispatch(addUser(user));
          }
        }}
        saveText="YES"
      />
      {!!showOverlay && (
        <div className="overlay-wrapper">
          <OverlaySpinner />
        </div>
      )}
    </div>
  );
};

export default User;
